/* eslint-disable react-refresh/only-export-components -- stable document-design
   primitives (tokens + block chrome) shared by the estimate and invoice DOM
   renderers; they never change at runtime, so Fast Refresh impact is nil. */
import type { CSSProperties, ReactNode } from 'react';
import type {
  EstimateDocContentBlock,
  EstimateDocSlotBlock,
} from '@notifi/shared-types/estimate-doc-template';

/**
 * The document-design language, in one place, for BOTH DOM renderers
 * (`EstimateDocumentRenderer` and `InvoiceDocumentRenderer`). Typographic tokens
 * lifted from the estimate-document design; the brand colour is a highlight
 * applied through `var(--doc-primary)`, body copy stays ink. Keeping the tokens
 * and block chrome here means the two documents share one visual system instead
 * of drifting apart line by line.
 */

export const MONO = '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
export const INK = '#0a0b0d';
export const INK_SOFT = '#2e2f33';
export const MUTED = '#6b6b70';
export const HAIRLINE = '#f3f2ed';
export const LINE = '#e5e4de';
// Struck list price (gray-400) and the member-savings note (emerald-700) — the
// same colour language the classic itemized document uses for accepted-member lines.
export const STRIKE = '#9ca3af';
export const MEMBER_SAVE = '#047857';

export const docLabelStyle: CSSProperties = {
  fontFamily: MONO,
  fontSize: '8px',
  fontWeight: 500,
  letterSpacing: '0.15em',
  textTransform: 'uppercase',
  color: MUTED,
};

// ─── Shared block chrome ────────────────────────────────────────────────────

export function BlockShell({
  block,
  children,
}: {
  block: EstimateDocContentBlock | EstimateDocSlotBlock;
  children: ReactNode;
}) {
  // Per-block content alignment. Cascades to the text blocks (company info,
  // bill-to, service address, notes, terms) and the letterhead; flex blocks
  // (logo, totals) handle it themselves.
  const align = block.kind === 'content' ? block.align : undefined;
  // The customer approval agreement links to "the terms" and scrolls to
  // `#estimate-terms` (ApproveDeclineSlot). The classic document anchors that id
  // on its terms section; the terms BLOCK must carry it too so the anchor is
  // guaranteed on design-rendered quotes. `scroll-mt-24` keeps the heading clear
  // of the sticky header, matching the classic.
  const isTerms = block.type === 'terms';
  return (
    <section
      data-block-type={block.type}
      data-block-id={block.id}
      id={isTerms ? 'estimate-terms' : undefined}
      className={isTerms ? 'scroll-mt-24' : undefined}
      style={align ? { textAlign: align } : undefined}
    >
      {block.heading ? (
        <h2
          className="mb-[9px]"
          style={{
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: INK,
            fontFamily: 'var(--doc-heading-font)',
          }}
        >
          {block.heading}
        </h2>
      ) : null}
      {children}
    </section>
  );
}

export function SectionLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="mb-[5px]" style={{ ...docLabelStyle, ...style }}>
      {children}
    </div>
  );
}

// A block's bold section heading, matching the heading BlockShell renders from
// `block.heading`. Used by prose/media blocks (scope, notes, terms, attachments)
// that source their title from the section field rather than `block.heading`.
export function BlockSectionHeading({ children }: { children: ReactNode }) {
  return (
    <h2
      className="mb-[9px]"
      style={{
        fontSize: '13px',
        fontWeight: 600,
        letterSpacing: '-0.015em',
        color: INK,
        fontFamily: 'var(--doc-heading-font)',
      }}
    >
      {children}
    </h2>
  );
}

export function DetailRow({
  label,
  value,
  labelCss,
  valueCss,
}: {
  label: ReactNode;
  value: ReactNode;
  labelCss?: CSSProperties;
  valueCss?: CSSProperties;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-[2px]">
      <span style={{ fontSize: '9px', color: MUTED, ...labelCss }}>{label}</span>
      <span style={{ fontSize: '10px', fontWeight: 500, color: INK, ...valueCss }}>{value}</span>
    </div>
  );
}
